import { SearchFilters } from '../core/types';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

export class SearchCache {
  private static cache = new Map<string, CacheEntry<unknown>>();
  private static readonly DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private static readonly LOCATION_CACHE_TTL = 10 * 60 * 1000; // 10 minutes for location-based searches
  private static readonly GENERAL_CACHE_TTL = 3 * 60 * 1000; // 3 minutes for general searches

  /**
   * Get cache TTL based on search type
   */
  private static getCacheTTL(filters: SearchFilters): number {
    // Location-based searches are more stable and can be cached longer
    if (filters.location || filters.locationText) {
      return this.LOCATION_CACHE_TTL;
    }
    
    // General searches might change more frequently
    if (filters.query || filters.holidayTypes || filters.displayTypes) {
      return this.GENERAL_CACHE_TTL;
    }
    
    return this.DEFAULT_CACHE_TTL;
  }

  /**
   * Enhanced cache key generation with better specificity
   */
  private static getCacheKey(operation: string, params: Record<string, unknown>): string {
    // Sort keys for consistent cache keys
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        if (params[key] !== undefined && params[key] !== null) {
          // Handle arrays and objects
          if (Array.isArray(params[key])) {
            result[key] = (params[key] as unknown[]).sort().join(',');
          } else if (typeof params[key] === 'object') {
            result[key] = JSON.stringify(params[key]);
          } else {
            result[key] = params[key];
          }
        }
        return result;
      }, {} as Record<string, unknown>);

    const keyString = JSON.stringify(sortedParams);
    
    // Include operation and create a shorter hash for performance
    return `${operation}:${this.hashString(keyString)}`;
  }

  /**
   * Simple hash function for cache keys
   */
  private static hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Enhanced cache management with TTL support
   */
  public static getCachedResult<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached) {
      const now = Date.now();
      if (now - cached.timestamp < cached.ttl) {
        return cached.data as T;
      } else {
        // Remove expired entry
        this.cache.delete(key);
      }
    }
    return null;
  }

  /**
   * Set cache with TTL support
   */
  public static setCachedResult<T>(
    operation: string, 
    params: Record<string, unknown>, 
    data: T, 
    filters?: SearchFilters
  ): void {
    const key = this.getCacheKey(operation, params);
    const ttl = filters ? this.getCacheTTL(filters) : this.DEFAULT_CACHE_TTL;
    
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Get cache key for external use
   */
  public static getKey(operation: string, params: Record<string, unknown>): string {
    return this.getCacheKey(operation, params);
  }

  /**
   * Clear all cache entries
   */
  public static clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear expired cache entries
   */
  public static clearExpiredEntries(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp >= entry.ttl) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  public static getCacheStats(): { size: number; expired: number } {
    const now = Date.now();
    let expired = 0;
    
    for (const entry of this.cache.values()) {
      if (now - entry.timestamp >= entry.ttl) {
        expired++;
      }
    }
    
    return {
      size: this.cache.size,
      expired
    };
  }
}